import { useMemo, useState } from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import { useParams } from 'react-router-dom';

import { ProceduralEventRow } from './procedural-event-row';
import { ProceduralReviewModal } from './procedural-review-modal';
import { ProceduralSourcePanel, SourceCitation } from './procedural-source-drawer';
import type { ProceduralTimelineEventSuggestion } from '../../../../../../../../backend/src/matter/procedural-timeline/procedural-timeline-event-suggestion.entity';
import type { ProceduralTimelineEvent } from '../../../../../../../../backend/src/matter/procedural-timeline/procedural-timeline-event.entity';
import { cn } from '@/new/lib/utils';
import { DocChronologyEmptyStateIllustration } from '@/new/pages/matters/_matterId/subsets/_subsetId/chronologies/doc/components/doc-chronology-empty-state-illustration';

interface ProceduralTimelineListProps {
  events: ProceduralTimelineEvent[];
  suggestions: ProceduralTimelineEventSuggestion[];
  isExtractionInFlight: boolean;
  isReviewOpen: boolean;
  onReviewOpenChange: (open: boolean) => void;
}

interface SortKey {
  // Primary key: event date. Items without a date sort to the bottom.
  date: number;
  // Backup keys for dated ties *and* for dateless items: order by where the
  // event came from in the source document (file name asc, then page asc) so
  // undated items appear in reading order grouped by document.
  fileName: string;
  pageNumber: number;
}

const FAR_FUTURE = Number.POSITIVE_INFINITY;

function toDateKey(date: Date | string | null | undefined): number {
  if (!date) return FAR_FUTURE;
  const ms = typeof date === 'string' ? new Date(date).getTime() : date.getTime();
  return Number.isFinite(ms) ? ms : FAR_FUTURE;
}

function eventSortKey(event: ProceduralTimelineEvent): SortKey {
  const chunk = event.sourceChunks?.[0];
  return {
    date: toDateKey(event.startDateTime),
    fileName: chunk?.file?.name ?? '',
    pageNumber: chunk?.pageNumber ?? 0,
  };
}

function compareSortKeys(a: SortKey, b: SortKey): number {
  if (a.date !== b.date) return a.date - b.date;
  if (a.fileName !== b.fileName) return a.fileName.localeCompare(b.fileName);
  return a.pageNumber - b.pageNumber;
}

// Editing is blocked only while a timetable is being processed (dates are
// actively changing). Editing is allowed with pending suggestions open — the
// edit dialog warns before saving and leaves the open suggestions untouched.
function getEditDisabledReason(isExtractionInFlight: boolean): string | null {
  if (isExtractionInFlight) {
    return 'A timetable is currently being processed. Wait for it to finish before editing events.';
  }
  return null;
}

export function ProceduralTimelineList({
  events,
  suggestions,
  isExtractionInFlight,
  isReviewOpen,
  onReviewOpenChange,
}: ProceduralTimelineListProps) {
  const { matterId } = useParams();
  const editDisabledReason = getEditDisabledReason(isExtractionInFlight);
  const [openSource, setOpenSource] = useState<SourceCitation[]>([]);

  const eventsById = useMemo(() => {
    const m: Record<string, ProceduralTimelineEvent> = {};
    for (const e of events) m[e.id] = e;
    return m;
  }, [events]);

  // Events that have a pending update suggestion, flagged inline so a reviewer
  // knows which rows a suggestion would change.
  const eventsWithPendingUpdate = useMemo(() => {
    const s = new Set<string>();
    for (const sug of suggestions) {
      if (sug.kind === 'update' && sug.existingEventId !== null) s.add(sug.existingEventId);
    }
    return s;
  }, [suggestions]);

  const sortedEvents = useMemo(
    () => [...events].sort((a, b) => compareSortKeys(eventSortKey(a), eventSortKey(b))),
    [events],
  );

  // First event in sort order that hasn't happened yet, for dot styling. Matches
  // the matter-overview preview: past = filled teal, next = teal ring, future/
  // undated = grey.
  const nextUpcomingIndex = useMemo(
    () => sortedEvents.findIndex((e) => e.startDateTime !== null && new Date(e.startDateTime).getTime() >= Date.now()),
    [sortedEvents],
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {events.length === 0 ? (
        <ReviewPromptState count={suggestions.length} />
      ) : (
        <div className="flex min-h-0 flex-1 items-stretch gap-4">
          <div className="min-w-0 flex-1 overflow-y-auto pr-1">
            <div className="flex flex-col gap-3 pl-3">
              <AnimatePresence initial={false}>
                {sortedEvents.map((event, index) => (
                  <motion.div
                    key={`event-${event.id}`}
                    layout="position"
                    initial={{ opacity: 0, scale: 0.98 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.98 }}
                    transition={{ duration: 0.2, ease: 'easeInOut' }}
                    className="group relative w-full pl-10"
                  >
                    <div
                      className={cn(
                        'absolute top-1/2 left-0.25 z-10 h-4 w-4 -translate-y-1/2 rounded-full border-2',
                        event.startDateTime === null
                          ? 'border-gray-200 bg-gray-50' // unknown date
                          : nextUpcomingIndex === -1 || index < nextUpcomingIndex
                            ? 'border-teal-400 bg-teal-100' // completed (past)
                            : index === nextUpcomingIndex
                              ? 'border-teal-400 bg-white' // next event to happen
                              : 'border-gray-200 bg-gray-50', // future
                      )}
                    />
                    <div className="absolute inset-0 -inset-y-3 w-0.5 translate-x-[8px] bg-gray-200 group-first:top-1/2 group-last:bottom-1/2"></div>

                    <ProceduralEventRow
                      event={event}
                      eventsById={eventsById}
                      onOpenSource={setOpenSource}
                      editDisabledReason={editDisabledReason}
                      hasPendingUpdate={eventsWithPendingUpdate.has(event.id)}
                      onReviewClick={() => onReviewOpenChange(true)}
                    />
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </div>

          <ProceduralSourcePanel citations={openSource} onClose={() => setOpenSource([])} />
        </div>
      )}

      {matterId !== undefined && (
        <ProceduralReviewModal
          open={isReviewOpen}
          onOpenChange={onReviewOpenChange}
          suggestions={suggestions}
          eventsById={eventsById}
          matterId={matterId}
        />
      )}
    </div>
  );
}

/** Shown when suggestions exist but nothing has been accepted yet: a prompt to review them. */
function ReviewPromptState({ count }: { count: number }) {
  return (
    <div className="mt-32 flex flex-col items-center">
      <DocChronologyEmptyStateIllustration />

      <p className="font-serif text-xl font-medium">
        {count} {count === 1 ? 'suggestion' : 'suggestions'} to review
      </p>

      <p className="mt-2 max-w-xl text-center text-balance text-gray-500">
        Review and accept the pending suggestions to build your procedural timeline.
      </p>
    </div>
  );
}
